import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRadixConnect } from "../../../radix_connect";
import {
  DCKSLAP_COMPONENT_ADDRESS,
  DCKSLAP_RESOURCE_ADDRESS,
  GBOF_RESOURCE_ADDRESS,
  RADIX_API_GATEWAY_URL,
  REDDICKS_RESSOURCE_ADDRESS,
} from "../../../constants";
import { DEX_SITE_URL } from "../../../config/siteContent";
import { useDckslapActivityStats } from "../../hooks/useDckslapActivityStats";
import { useReddicksDexAnalytics } from "../../hooks/useReddicksDexAnalytics";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import ClaimBanner from "./components/ClaimBanner";
import DckslapCoreStatsSection from "./components/DckslapCoreStatsSection";
import LeaderboardTableSection from "./components/LeaderboardTableSection";
import ReddicksOverviewSection from "./components/ReddicksOverviewSection";
import ReddicksTopPairsTable from "./components/ReddicksTopPairsTable";
import ReddicksTokenSections from "./components/ReddicksTokenSections";
import ReddicksTrendCard from "./components/ReddicksTrendCard";
import UnifiedTransactionsHero from "./components/UnifiedTransactionsHero";
import { formatNumber } from "./formatters";
import { buildLeaderboardRows } from "./leaderboardData";

const DCKSLAP_STATS_CATEGORY = "dckslap-stats";
const REDDICKS_STATS_CATEGORY = "reddicks-stats";

export const LeaderboardContent = () => {
  const navigate = useNavigate();
  const { connected, selectedAccount } = useRadixConnect();
  const {
    dckslapData,
    gbofData,
    stats,
    isLoading,
    error,
    hasCachedData,
  } = useDckslapActivityStats();
  const {
    overview: reddicksOverview,
    trendRows: reddicksTrendRows,
    tokenMarkets: reddicksTokenMarkets,
    topPairs: reddicksTopPairs,
    isLoading: reddicksIsLoading,
    error: reddicksError,
    hasCachedData: reddicksHasCachedData,
    lastUpdated: reddicksLastUpdated,
    isFresh: reddicksIsFresh,
  } = useReddicksDexAnalytics();

  const [activeCategory, setActiveCategory] = useState(DCKSLAP_STATS_CATEGORY);
  const [activeLeaderboardTab, setActiveLeaderboardTab] = useState("dckslap");
  const [currentPage, setCurrentPage] = useState(1);
  const [gbofMinted, setGbofMinted] = useState(0);
  const [gbofMintedMonthData, setGbofMintedMonthData] = useState({});
  const [slapSentData, setSlapSentData] = useState({});
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("desc");

  const now = new Date();
  const GBOF_MINTED_MONTH = now.getMonth() + 1;
  const GBOF_MINTED_YEAR = now.getFullYear();
  const currentMonthName = new Date(
    GBOF_MINTED_YEAR,
    GBOF_MINTED_MONTH - 1,
  ).toLocaleString("en-US", { month: "long" });

  useEffect(() => {
    let isMounted = true;

    const fetchGbofMinted = async () => {
      let gbofMintedCount = 0;
      let cursor = null;
      const gbofDataForMonth = {};
      const monthIndex = GBOF_MINTED_MONTH - 1;
      const monthStart = new Date(Date.UTC(GBOF_MINTED_YEAR, monthIndex, 1, 0, 0, 0));
      const monthEnd = new Date(
        Date.UTC(GBOF_MINTED_YEAR, monthIndex + 1, 0, 23, 59, 59, 999),
      );
      const today = new Date();

      const requestData = {
        cursor,
        order: "Asc",
        from_ledger_state: {
          timestamp: monthStart.toISOString().replace(/\.000Z$/, "Z"),
        },
        opt_ins: {
          balance_changes: true,
          receipt_output: false,
        },
        limit_per_page: 500,
        transaction_status_filter: "Success",
        events_filter: [
          {
            event: "Deposit",
            resource_address: GBOF_RESOURCE_ADDRESS,
          },
        ],
        affected_global_entities_filter: [
          DCKSLAP_COMPONENT_ADDRESS,
          GBOF_RESOURCE_ADDRESS,
        ],
        balance_change_resources_filter: [GBOF_RESOURCE_ADDRESS],
      };

      if (monthEnd < today) {
        requestData.at_ledger_state = {
          timestamp: monthEnd.toISOString().replace(/\.000Z$/, "Z"),
        };
      }

      try {
        do {
          requestData.cursor = cursor;
          const response = await fetch(`${RADIX_API_GATEWAY_URL}/stream/transactions`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestData),
          });

          if (!response.ok) {
            throw new Error(`Network response was not ok: ${response.statusText}`);
          }

          const data = await response.json();
          const items = Array.isArray(data?.items) ? data.items : [];
          gbofMintedCount += items.length;

          items.forEach((item) => {
            const fungibleChanges = item?.balance_changes?.fungible_balance_changes;
            if (!Array.isArray(fungibleChanges)) {
              return;
            }

            if (fungibleChanges.length === 2) {
              const userGotGbof = fungibleChanges.find(
                (change) =>
                  change.resource_address === GBOF_RESOURCE_ADDRESS &&
                  change.balance_change === "1" &&
                  typeof change.entity_address === "string" &&
                  change.entity_address.startsWith("account_"),
              );

              if (!userGotGbof) {
                return;
              }

              const userGotDckslap = fungibleChanges.find(
                (change) =>
                  change.resource_address === DCKSLAP_RESOURCE_ADDRESS &&
                  change.balance_change === "1" &&
                  change.entity_address === userGotGbof.entity_address,
              );
              const userPaidDckslap = fungibleChanges.find(
                (change) =>
                  change.resource_address === DCKSLAP_RESOURCE_ADDRESS &&
                  change.balance_change === "-1" &&
                  change.entity_address === userGotGbof.entity_address,
              );

              if (
                (userPaidDckslap && !userGotDckslap) ||
                (!userPaidDckslap && userGotDckslap)
              ) {
                gbofDataForMonth[userGotGbof.entity_address] =
                  (gbofDataForMonth[userGotGbof.entity_address] || 0) + 1;
              }

              return;
            }

            if (fungibleChanges.length === 4) {
              const userPaidReddicks = fungibleChanges.find(
                (change) =>
                  change.resource_address === REDDICKS_RESSOURCE_ADDRESS &&
                  change.balance_change === "-20" &&
                  typeof change.entity_address === "string" &&
                  change.entity_address.startsWith("account_"),
              );

              if (!userPaidReddicks) {
                return;
              }

              const userGotDckslap = fungibleChanges.find(
                (change) =>
                  change.resource_address === DCKSLAP_RESOURCE_ADDRESS &&
                  change.balance_change === "1" &&
                  change.entity_address === userPaidReddicks.entity_address,
              );
              const userGotGbof = fungibleChanges.find(
                (change) =>
                  change.resource_address === GBOF_RESOURCE_ADDRESS &&
                  change.balance_change === "1" &&
                  change.entity_address === userPaidReddicks.entity_address,
              );
              const componentGotReddicks = fungibleChanges.find(
                (change) =>
                  change.resource_address === REDDICKS_RESSOURCE_ADDRESS &&
                  change.balance_change === "20" &&
                  change.entity_address === DCKSLAP_COMPONENT_ADDRESS,
              );

              if (userGotDckslap && userGotGbof && componentGotReddicks) {
                gbofDataForMonth[userPaidReddicks.entity_address] =
                  (gbofDataForMonth[userPaidReddicks.entity_address] || 0) + 1;
              }
            }
          });

          cursor = data?.next_cursor || null;
        } while (cursor && isMounted);

        if (isMounted) {
          setGbofMinted(gbofMintedCount);
          setGbofMintedMonthData(gbofDataForMonth);
        }
      } catch (fetchError) {
        console.error("Failed to fetch monthly GBOF minted activity:", fetchError);
      }
    };

    const fetchSlapSent = async () => {
      let cursor = null;
      const slapSentDataLocal = {};
      const monthIndex = GBOF_MINTED_MONTH - 1;
      const monthStart = new Date(Date.UTC(GBOF_MINTED_YEAR, monthIndex, 1, 0, 0, 0));
      const monthEnd = new Date(
        Date.UTC(GBOF_MINTED_YEAR, monthIndex + 1, 0, 23, 59, 59, 999),
      );
      const today = new Date();

      const requestData = {
        cursor,
        order: "Asc",
        from_ledger_state: {
          timestamp: monthStart.toISOString().replace(/\.000Z$/, "Z"),
        },
        opt_ins: {
          balance_changes: true,
          receipt_output: false,
        },
        limit_per_page: 500,
        transaction_status_filter: "Success",
        manifest_class_filter: { class: "Transfer" },
        events_filter: [
          {
            event: "Withdrawal",
            resource_address: DCKSLAP_RESOURCE_ADDRESS,
          },
          {
            event: "Deposit",
            resource_address: DCKSLAP_RESOURCE_ADDRESS,
          },
        ],
      };

      if (monthEnd < today) {
        requestData.at_ledger_state = {
          timestamp: monthEnd.toISOString().replace(/\.000Z$/, "Z"),
        };
      }

      try {
        do {
          requestData.cursor = cursor;
          const response = await fetch(`${RADIX_API_GATEWAY_URL}/stream/transactions`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestData),
          });

          if (!response.ok) {
            throw new Error(`Network response was not ok: ${response.statusText}`);
          }

          const data = await response.json();
          const items = Array.isArray(data?.items) ? data.items : [];

          items.forEach((item) => {
            const fungibleChanges = item?.balance_changes?.fungible_balance_changes;
            if (!Array.isArray(fungibleChanges) || fungibleChanges.length !== 2) {
              return;
            }

            const [first, second] = fungibleChanges;
            const areDckslapTransfers =
              first.resource_address === DCKSLAP_RESOURCE_ADDRESS &&
              second.resource_address === DCKSLAP_RESOURCE_ADDRESS &&
              first.entity_address !== second.entity_address;

            if (!areDckslapTransfers) {
              return;
            }

            if (first.balance_change === "-1" && second.balance_change === "1") {
              slapSentDataLocal[first.entity_address] =
                (slapSentDataLocal[first.entity_address] || 0) + 1;
            } else if (first.balance_change === "1" && second.balance_change === "-1") {
              slapSentDataLocal[second.entity_address] =
                (slapSentDataLocal[second.entity_address] || 0) + 1;
            }
          });

          cursor = data?.next_cursor || null;
        } while (cursor && isMounted);

        if (isMounted) {
          setSlapSentData(slapSentDataLocal);
        }
      } catch (fetchError) {
        console.error("Failed to fetch monthly slap sent activity:", fetchError);
      }
    };

    fetchGbofMinted();
    fetchSlapSent();

    return () => {
      isMounted = false;
    };
  }, [GBOF_MINTED_MONTH, GBOF_MINTED_YEAR]);

  const leaderboardRows = buildLeaderboardRows({
    activeTab: activeLeaderboardTab,
    dckslapData,
    gbofData,
    gbofMintedMonthData,
    slapSentData,
    sortColumn,
    sortDirection,
  });
  const totalPages = Math.max(1, Math.ceil(leaderboardRows.length / 10));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((direction) => (direction === "asc" ? "desc" : "asc"));
      return;
    }

    setSortColumn(column);
    setSortDirection("desc");
  };

  const handlePageChange = (direction) => {
    setCurrentPage((page) => {
      if (direction === "prev") {
        return Math.max(1, page - 1);
      }

      if (direction === "next") {
        return Math.min(totalPages, page + 1);
      }

      return page;
    });
  };

  const handleLeaderboardTabChange = (tab) => {
    setActiveLeaderboardTab(tab);
    setCurrentPage(1);
    setSortColumn(null);
    setSortDirection("desc");
  };

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-intro-card">
        <div className="leaderboard-intro-copy">
          <span className="leaderboard-intro-kicker">Network Impact Center</span>
          <h1 className="leaderboard-intro-title">Leaderboard and Market Analytics</h1>
          <p className="leaderboard-intro-subtitle">
            Track transaction contribution and official RSwap market health in one view.
          </p>
        </div>
        <div className="leaderboard-intro-chips">
          <span className="leaderboard-intro-chip">
            Unified Tx: <strong>{formatNumber(stats.grandTotalVolume)}</strong>
          </span>
          <span className="leaderboard-intro-chip leaderboard-intro-chip-muted">
            Counts = events, USD = analytics snapshots
          </span>
        </div>
      </div>

      <Tabs
        value={activeCategory}
        onValueChange={setActiveCategory}
        className="space-y-6"
      >
        <TabsList className="leaderboard-parent-tabs">
          <TabsTrigger
            value={DCKSLAP_STATS_CATEGORY}
            className="leaderboard-parent-trigger"
          >
            DCKSLAP Stats
          </TabsTrigger>
          <TabsTrigger
            value={REDDICKS_STATS_CATEGORY}
            className="leaderboard-parent-trigger"
          >
            Reddicks Stats
          </TabsTrigger>
        </TabsList>

        <TabsContent value={DCKSLAP_STATS_CATEGORY} className="space-y-6">
          <DckslapCoreStatsSection
            stats={stats}
            gbofMinted={gbofMinted}
            currentMonthName={currentMonthName}
          />

          <UnifiedTransactionsHero stats={stats} mode="dckslap" />

          <ClaimBanner onClick={() => navigate("/dckslap/slapgenerator")} />

          <LeaderboardTableSection
            activeTab={activeLeaderboardTab}
            onTabChange={handleLeaderboardTabChange}
            rows={leaderboardRows}
            isLoading={isLoading}
            error={error}
            hasCachedData={hasCachedData}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            currentMonthName={currentMonthName}
            connected={connected}
            selectedAccount={selectedAccount}
          />
        </TabsContent>

        <TabsContent value={REDDICKS_STATS_CATEGORY} className="space-y-6">
          <ReddicksOverviewSection
            stats={stats}
            overview={reddicksOverview}
            isLoading={reddicksIsLoading}
            hasCachedData={reddicksHasCachedData}
            error={reddicksError}
            lastUpdated={reddicksLastUpdated}
            isFresh={reddicksIsFresh}
            dexSiteUrl={DEX_SITE_URL}
          />

          <UnifiedTransactionsHero stats={stats} mode="reddicks" />

          <ReddicksTrendCard
            trendRows={reddicksTrendRows}
            isLoading={reddicksIsLoading}
            hasCachedData={reddicksHasCachedData}
          />

          <ReddicksTopPairsTable
            topPairs={reddicksTopPairs}
            isLoading={reddicksIsLoading}
            hasCachedData={reddicksHasCachedData}
          />

          <ReddicksTokenSections
            tokenMarkets={reddicksTokenMarkets}
            dexSiteUrl={DEX_SITE_URL}
          />
        </TabsContent>
      </Tabs>

      <div className="leaderboard-footer">
        {activeCategory === DCKSLAP_STATS_CATEGORY ? (
          <p>
            Data powered by{" "}
            <a
              href="https://www.radxplorer.com/projects/dckslap"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Radxplorer
            </a>
          </p>
        ) : (
          <p>
            Data powered by{" "}
            <a
              href={DEX_SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              RSwap Analytics
            </a>{" "}
            and{" "}
            <a
              href="https://www.radxplorer.com/projects/dckslap"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Radxplorer
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

export default LeaderboardContent;
